/**
 * The queue's search box: sales type locally, and the search term reaches the URL (and
 * so the request) after a short pause. The URL stays the truth:
 *
 * - When the URL changes from outside (a sidebar link, "Clear filters", browser Back or
 *   Forward, a shared link), the box shows the URL's term and a pause still pending is
 *   cancelled, so an old term can never come back into the new view. That holds also
 *   when the term itself did not change (no term before, none after).
 * - When the URL changes because of the box's own write, the box keeps what sales typed
 *   (a trailing space while they type the next word is not taken away).
 *
 * End-user review of 11 Oct 2026 (F12): search for "valve", then the sidebar "Back for
 * you" link. The URL and the request lost the term but the box still said "valve".
 */

export interface SearchDraftDeps {
  /** Show this text in the box. */
  setText(text: string): void;
  /** Put this term into the URL; an empty term removes it. */
  writeTerm(term: string): void;
  schedule(run: () => void, ms: number): number;
  cancel(handle: number): void;
}

export class SearchDraft {
  private timer: number | undefined;
  /** The term this box last wrote, until the URL shows it. */
  private written: string | null = null;
  private urlTerm: string;
  private urlKey: string | null = null;
  private readonly deps: SearchDraftDeps;
  private readonly delayMs: number;
  private readonly maxLength: number;

  constructor(urlTerm: string, deps: SearchDraftDeps, delayMs: number, maxLength: number) {
    this.urlTerm = urlTerm;
    this.deps = deps;
    this.delayMs = delayMs;
    this.maxLength = maxLength;
  }

  /** True while a typed term waits for its pause. */
  get pending(): boolean {
    return this.timer !== undefined;
  }

  /** Sales typed `value`: show it now, write the term after the pause. */
  type(value: string): void {
    this.deps.setText(value);
    this.stop();
    this.timer = this.deps.schedule(() => {
      this.timer = undefined;
      const term = value.trim().slice(0, this.maxLength);
      if (term === this.urlTerm) {
        this.written = null;
        return;
      }
      this.written = term;
      this.deps.writeTerm(term);
    }, this.delayMs);
  }

  /**
   * The URL is now `urlKey` (its whole search string) with the term `term`. Returns true
   * when the change came from outside the box.
   */
  urlChanged(term: string, urlKey: string = term): boolean {
    const first = this.urlKey === null;
    if (urlKey === this.urlKey || (first && term === this.urlTerm)) {
      this.urlKey = urlKey;
      return false;
    }
    this.urlKey = urlKey;
    this.urlTerm = term;
    if (this.written !== null && term === this.written) {
      this.written = null;
      return false;
    }
    this.written = null;
    this.stop();
    this.deps.setText(term);
    return true;
  }

  /** "Clear filters": empty box, nothing pending (the caller clears the URL). */
  clear(): void {
    this.stop();
    this.written = null;
    this.deps.setText("");
  }

  /** The page goes away: nothing may be written after it. */
  dispose(): void {
    this.stop();
  }

  private stop(): void {
    if (this.timer !== undefined) {
      this.deps.cancel(this.timer);
      this.timer = undefined;
    }
  }
}
